import { useEffect, useRef, useState } from 'react';
import { Mic, MicOff, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

/* eslint-disable @typescript-eslint/no-explicit-any */
type SR = any;

interface Props {
  onTranscript: (text: string) => void;
  className?: string;
  /** Icon-only pulsing variant for use inside textareas. */
  compact?: boolean;
}

/**
 * Mic button that uses the browser Web Speech API to dictate into a field.
 * Falls back gracefully when unsupported (Safari iOS, some PWAs).
 */
export function QuickCaptureVoiceButton({ onTranscript, className, compact }: Props) {
  const [supported, setSupported] = useState(false);
  const [listening, setListening] = useState(false);
  const recRef = useRef<SR | null>(null);

  useEffect(() => {
    const w = window as any;
    const Ctor = w.SpeechRecognition || w.webkitSpeechRecognition;
    setSupported(!!Ctor);
  }, []);

  if (!supported) return null;

  const start = () => {
    const w = window as any;
    const Ctor = w.SpeechRecognition || w.webkitSpeechRecognition;
    const rec: SR = new Ctor();
    rec.continuous = false;
    rec.interimResults = false;
    rec.lang = 'en-US';
    rec.onresult = (e: any) => {
      const text = Array.from(e.results)
        .map((r: any) => r[0]?.transcript ?? '')
        .join(' ')
        .trim();
      if (text) onTranscript(text);
    };
    rec.onerror = (e: any) => {
      setListening(false);
      if (e.error !== 'aborted') toast.error('Mic error', { description: e.error });
    };
    rec.onend = () => setListening(false);
    recRef.current = rec;
    setListening(true);
    try { rec.start(); } catch { setListening(false); }
  };

  const stop = () => {
    try { recRef.current?.stop(); } catch { /* noop */ }
    setListening(false);
  };

  if (compact) {
    return (
      <button
        type="button"
        onClick={listening ? stop : start}
        aria-label={listening ? 'Stop dictation' : 'Dictate'}
        className={
          'h-8 w-8 rounded-full flex items-center justify-center transition-colors ' +
          (listening
            ? 'bg-primary/20 text-primary animate-pulse'
            : 'bg-muted/50 text-muted-foreground hover:text-primary hover:bg-primary/10') +
          (className ? ' ' + className : '')
        }
      >
        {listening ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
      </button>
    );
  }

  return (
    <Button
      type="button"
      size="sm"
      variant={listening ? 'default' : 'outline'}
      className={className}
      onClick={listening ? stop : start}
      aria-label={listening ? 'Stop dictation' : 'Dictate'}
    >
      {listening ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mic className="h-4 w-4" />}
      {listening ? 'Listening…' : 'Dictate'}
    </Button>
  );
}